import Popover from 'react-tiny-popover';
import React, {useState} from 'react';
import {ActionList} from '../components';
import {useApolloClient} from '@apollo/react-hooks';
import {
  ALBUM_VIEW_ALBUM_QUERY,
  CATALOG_ALBUM_VIEW_ALBUM_QUERY,
} from '../components/Pages/Library/Pages/AlbumView/graphql';
import {useQueueContext} from '../context/QueueProvider';
import {
  CATALOG_PLAYLIST_VIEW_QUERY,
  PLAYLIST_VIEW_QUERY,
} from '../components/Pages/Library/Pages/PlaylistView/graphql';
import {useObsidianContext} from '../context/ObsidianProvider';
import {Song} from '../types';

interface Options {
  id: string;
  resourceType:
    | 'catalog-album'
    | 'library-album'
    | 'catalog-playlist'
    | 'library-playlist';
}

const queriesMap = {
  'catalog-album': CATALOG_ALBUM_VIEW_ALBUM_QUERY,
  'library-album': ALBUM_VIEW_ALBUM_QUERY,
  'catalog-playlist': CATALOG_PLAYLIST_VIEW_QUERY,
  'library-playlist': PLAYLIST_VIEW_QUERY,
};

const POPOVER_DEFAULT_OPTIONS = {
  visible: false,
  top: 0,
  left: 0,
};

function useMusicContextMenu({id, resourceType}: Options) {
  const {
    playAllNext,
    playAllLater,
    setLoading: setQueueLoading,
    setUpNext,
  } = useQueueContext();
  const [popoverOptions, setPopoverOptions] = useState(POPOVER_DEFAULT_OPTIONS);
  const client = useApolloClient();
  const {obsidian} = useObsidianContext();

  const tracksFromResource = (
    data: any,
    resourceType: Options['resourceType'],
  ): Song[] => {
    switch (resourceType) {
      case 'catalog-album':
        return data.music?.album?.tracks;
      case 'library-album':
        return data.me?.library?.album?.tracks;
      case 'catalog-playlist':
        return data.music?.playlist?.tracks;
      case 'library-playlist':
        return data.me?.library?.playlist?.tracks;
    }
  };

  const handlePlay = async (
    when: 'now' | 'next' | 'later',
    resourceType: Options['resourceType'],
  ) => {
    setQueueLoading(true);
    setPopoverOptions(POPOVER_DEFAULT_OPTIONS);
    const {data} = await client.query({
      query: queriesMap[resourceType],
      variables: {id: id},
    });

    const tracks = tracksFromResource(data, resourceType);

    if (tracks.length < 0) return;

    switch (when) {
      case 'now':
        obsidian.play(tracks[0]);
        setUpNext(tracks.slice(1));
        break;
      case 'next':
        playAllNext(tracks);
        break;
      case 'later':
        playAllLater(tracks);
        break;
    }
    setQueueLoading(false);
  };

  const actionListItems = [
    {
      id: '1',
      content: 'Play',
      onAction: () => {
        handlePlay('now', resourceType);
      },
    },
    {
      id: '2',
      content: 'Play Next',
      onAction: () => {
        handlePlay('next', resourceType);
      },
    },
    {
      id: '3',
      content: 'Play Later',
      onAction: () => {
        handlePlay('later', resourceType);
      },
    },
    {
      id: '4',
      content: 'Like',
      disabled: true,
      onAction: () => {},
    },
    {
      id: '5',
      content: 'Share',
      disabled: true,
      onAction: () => {},
    },
  ];

  const popoverProps = {
    isOpen: popoverOptions.visible,
    content: <ActionList items={actionListItems} />,
    containerStyle: {
      boxShadow: '0 2px 6px 1px rgba(63, 63, 68, 0.2)',
      borderRadius: '5px',
      zIndex: '1',
    },
    contentLocation: {left: popoverOptions.left, top: popoverOptions.top},
    onClickOutside: () => {
      setPopoverOptions(POPOVER_DEFAULT_OPTIONS);
    },
    transitionDuration: 0.15,
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setPopoverOptions({visible: true, left: e.clientX, top: e.clientY});
  };

  return {
    Popover,
    popoverProps,
    onContextMenu: handleContextMenu,
  };
}

export default useMusicContextMenu;
